import { AnimatePresence, motion } from 'framer-motion'

import type { ClientUiModelOption } from '../../api/platformConfig'
import { SelectMenu } from '../SelectMenu'
import { createSectionLabelClass } from './createPanelTokens'

function SkeletonRow({ angular }: { angular: boolean }) {
  return (
    <div
      className={[
        'h-10 w-full animate-pulse',
        angular ? 'rounded-none' : 'rounded-lg',
        'bg-[color-mix(in_srgb,var(--line)_38%,transparent)]',
      ].join(' ')}
      aria-hidden
    />
  )
}

export type SelectMenuOption = {
  value: string
  label: string
  description?: string
}

export type ImageGenParamsSectionProps = {
  angular: boolean
  platformOptions: SelectMenuOption[]
  platformId: string
  onPlatformChange: (v: string) => void
  modelMenuOptions: SelectMenuOption[]
  modelId: string
  onModelChange: (v: string) => void
  aspectMenuOptions: SelectMenuOption[] | null
  aspectRatio: string
  onAspectRatioChange: (v: string) => void
  apiSize: string
  onApiSizeChange: (v: string) => void
  sizeMenuOptions: SelectMenuOption[]
  /** 模型维度模式 — size 显示尺寸下拉，aspect_ratio 显示比例下拉 */
  dimensionMode: 'size' | 'aspect_ratio'
  /** 模型额外参数选项 */
  extraOptions?: ClientUiModelOption[]
  /** 额外参数值 */
  extraValues: Record<string, string>
  /** 额外参数变更回调 */
  onExtraValueChange: (key: string, value: string) => void
}

export function ImageGenParamsSection(props: ImageGenParamsSectionProps) {
  const {
    angular,
    platformOptions,
    platformId,
    onPlatformChange,
    modelMenuOptions,
    modelId,
    onModelChange,
    aspectMenuOptions,
    aspectRatio,
    onAspectRatioChange,
    apiSize,
    onApiSizeChange,
    sizeMenuOptions,
    dimensionMode,
    extraOptions,
    extraValues,
    onExtraValueChange,
  } = props

  // 按 dimensionMode 决定显示哪个控件，不看 options 是否为空
  const showSize = dimensionMode === 'size'
  const showAspect = dimensionMode === 'aspect_ratio'
  const hasMultiplePlatforms = platformOptions.length > 1
  const hasExtraOptions = (extraOptions?.length ?? 0) > 0
  const extraMotionKey = hasExtraOptions
    ? extraOptions!.map((o) => o.key).join('\0')
    : 'none'

  return (
    <div className="flex flex-col gap-4">
      {hasMultiplePlatforms ? (
          <>
            <section aria-labelledby="create-param-platform-heading">
              <h3 id="create-param-platform-heading" className={createSectionLabelClass()}>
                平台
              </h3>
              <div className="mt-2">
                <SelectMenu
                  id="image-platform"
                  ariaLabel="AI 平台"
                  value={platformId}
                  onChange={onPlatformChange}
                  options={platformOptions}
                  angular={angular}
                />
              </div>
            </section>
            <div className="h-px w-full bg-[color-mix(in_srgb,var(--line)_65%,transparent)]" aria-hidden />
          </>
        ) : null}

        {/* 模型独占一行 */}
        <section aria-labelledby="create-param-model-heading" className="min-w-0">
          <h3 id="create-param-model-heading" className={createSectionLabelClass()}>
            模型
          </h3>
          <div className="mt-2">
            <SelectMenu
              id="image-model"
              ariaLabel="图像模型"
              value={modelId}
              onChange={onModelChange}
              options={modelMenuOptions}
              angular={angular}
            />
          </div>
        </section>

        {/* sm+：尺寸或比例独占一行（二选一） */}
        <div className="grid grid-cols-1 gap-y-4 sm:grid-cols-1 sm:gap-x-3">
          {showSize ? (
            <section aria-labelledby="create-param-size-heading" className="min-w-0">
              <h3 id="create-param-size-heading" className={createSectionLabelClass()}>
                尺寸
              </h3>
              <div className="mt-2">
                <SelectMenu
                  id="image-size"
                  ariaLabel="输出尺寸"
                  value={apiSize}
                  onChange={onApiSizeChange}
                  options={sizeMenuOptions}
                  angular={angular}
                />
              </div>
            </section>
          ) : null}

          {showAspect && aspectMenuOptions ? (
            <section
              aria-labelledby="create-param-aspect-heading"
              className="min-w-0"
            >
              <h3 id="create-param-aspect-heading" className={createSectionLabelClass()}>
                比例
              </h3>
              <div className="mt-2">
                <SelectMenu
                  id="image-aspect"
                  ariaLabel="宽高比"
                  value={aspectRatio}
                  onChange={onAspectRatioChange}
                  options={aspectMenuOptions}
                  angular={angular}
                />
              </div>
            </section>
          ) : null}
        </div>

        <AnimatePresence initial={false} mode="popLayout">
          {hasExtraOptions ? (
            <motion.div
              key={extraMotionKey}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 6 }}
              transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
              className="flex flex-col gap-4"
            >
              <div className="h-px w-full bg-[color-mix(in_srgb,var(--line)_65%,transparent)]" aria-hidden />
              <section aria-labelledby="create-param-extra-heading">
                <h3 id="create-param-extra-heading" className={createSectionLabelClass()}>
                  高级选项
                </h3>
                <div className="mt-2 grid grid-cols-1 gap-3 min-[380px]:grid-cols-2">
                  {extraOptions!.map((option) => (
                    <div key={option.key} className="flex min-w-0 flex-col gap-1">
                      <label className="text-xs text-secondary">{option.label}</label>
                      {option.type === 'boolean' ? (
                        <label className="flex cursor-pointer items-center gap-2 py-1.5">
                          <input
                            type="checkbox"
                            id={`image-extra-${option.key}`}
                            checked={(extraValues[option.key] ?? option.defaultValue ?? 'false') === 'true'}
                            onChange={(e) => onExtraValueChange(option.key, String(e.target.checked))}
                            className="h-4 w-4 rounded border-line text-primary focus:ring-primary"
                          />
                          <span className="text-sm text-secondary">启用</span>
                        </label>
                      ) : (option.options?.length ?? 0) > 0 ? (
                        <SelectMenu
                          id={`image-extra-${option.key}`}
                          ariaLabel={option.label}
                          value={extraValues[option.key] ?? option.defaultValue ?? ''}
                          onChange={(v) => onExtraValueChange(option.key, v)}
                          options={option.options.map((o) => ({
                            value: o.value,
                            label: o.label,
                            description: o.description,
                          }))}
                          angular={angular}
                        />
                      ) : option.type === 'number' ? (
                        <input
                          type="number"
                          id={`image-extra-${option.key}`}
                          value={extraValues[option.key] ?? option.defaultValue ?? ''}
                          onChange={(e) => onExtraValueChange(option.key, e.target.value)}
                          className="w-full rounded-md border border-line bg-surface px-2 py-1.5 text-sm text-primary outline-none focus:border-primary focus:ring-1 focus:ring-primary"
                          placeholder={option.label}
                        />
                      ) : (
                        <p className="py-1.5 text-xs text-ink-soft">无可用选项</p>
                      )}
                    </div>
                  ))}
                </div>
              </section>
            </motion.div>
          ) : null}
        </AnimatePresence>
    </div>
  )
}

/** 首次拉取 `/v1/platform-configs` 前展示，避免用本地降级表渲染下拉后再被服务端列表替换造成闪烁 */
export function ImageGenParamsSectionSkeleton({ angular }: { angular: boolean }) {
  return (
    <div className="flex flex-col gap-4" aria-busy="true">
      <span className="sr-only" role="status">
        正在加载平台与模型配置
      </span>
        <div className="grid grid-cols-1 gap-y-4 sm:grid-cols-2 sm:gap-x-3">
          <section aria-labelledby="create-param-model-skel">
            <h3 id="create-param-model-skel" className={createSectionLabelClass()}>
              模型
            </h3>
            <div className="mt-2">
              <SkeletonRow angular={angular} />
            </div>
          </section>
          <section aria-labelledby="create-param-size-skel">
            <h3 id="create-param-size-skel" className={createSectionLabelClass()}>
              尺寸
            </h3>
            <div className="mt-2">
              <SkeletonRow angular={angular} />
            </div>
          </section>
          <section className="sm:col-span-2" aria-labelledby="create-param-aspect-skel">
            <h3 id="create-param-aspect-skel" className={createSectionLabelClass()}>
              比例
            </h3>
            <div className="mt-2">
              <SkeletonRow angular={angular} />
            </div>
          </section>
        </div>
    </div>
  )
}
